import { useState, useMemo } from "react";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useQuestionnaire } from "@/components/questionnaire/QuestionnaireContext";
import { gaEvent, metaEvent } from "@/lib/analytics";
import logoIconWhite from "@/assets/logo-icon-white.webp";

// Internal sample rate for hero estimator. Not displayed to the user.
// Full rate/APR disclosures belong on the dedicated calculator page, not here.
const SAMPLE_RATE = 6.5;
// Future: add SAMPLE_APR here if APR display is ever needed on the hero.
// const SAMPLE_APR = 6.75;

const HeroSection = () => {
  const { openQuestionnaire } = useQuestionnaire();
  const [homePrice, setHomePrice] = useState(350000);
  const [downPayment, setDownPayment] = useState(70000);
  const [term, setTerm] = useState(30);
  const [assumptionsOpen, setAssumptionsOpen] = useState(false);

  const monthlyPayment = useMemo(() => {
    const principal = homePrice - downPayment;
    const monthlyRate = SAMPLE_RATE / 100 / 12;
    const n = term * 12;
    if (monthlyRate === 0) return principal / n;
    return (principal * (monthlyRate * Math.pow(1 + monthlyRate, n))) / (Math.pow(1 + monthlyRate, n) - 1);
  }, [homePrice, downPayment, term]);

  const fmt = (n: number) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

  return (
    <section className="relative overflow-hidden bg-primary">
      {/* Background watermark logo */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <img
          src={logoIconWhite}
          alt=""
          width="420"
          height="420"
          decoding="async"
          fetchPriority="low"
          className="w-[420px] h-[420px] shrink-0 object-contain opacity-[0.03]"
        />
      </div>
      {/* Subtle dot texture */}
      <div className="absolute inset-0 opacity-[0.015]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
        backgroundSize: '32px 32px',
      }} />

      <div className="container-wide relative z-10 w-full px-6 py-16 md:px-10 md:py-20 lg:px-16 xl:px-20">
        <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-10 lg:gap-14 items-center">
          {/* Left — copy & actions */}
          <div className="space-y-7">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="h-px w-8 bg-silver/30" />
              <p className="font-body text-xs sm:text-sm font-semibold uppercase tracking-[0.25em] text-silver/70">
                Minnesota Mortgage Broker <span className="text-silver/50">·</span> Champlin, MN <span className="text-silver/50">·</span>
                <span className="text-silver/60 font-normal tracking-[0.15em]"> NMLS #2817074</span>
              </p>
            </div>

            <h1 className="font-display text-5xl font-bold leading-[1.05] text-primary-foreground md:text-6xl lg:text-[4.75rem]">
              Understand Your Mortgage <br />
              <span className="text-silver italic font-normal">Before You Commit</span>
            </h1>

            <p className="font-body text-lg text-primary-foreground/75 max-w-lg leading-relaxed">
              Clear guidance, a transparent process, and honest answers designed to make home financing simple.
            </p>

            <div className="flex flex-wrap gap-3 pt-1">
              <Button
                variant="hero-outline"
                size="lg"
                onClick={() => {
                  gaEvent("cta_click", { cta_id: "personalized_rate", location: "hero" });
                  metaEvent("CTAClick", "custom", { cta_id: "personalized_rate", location: "hero" });
                  openQuestionnaire();
                }}
              >
                Check Your Personalized Rate
              </Button>
              <Button
                variant="silver"
                size="lg"
                asChild
                onClick={() => {
                  gaEvent("cta_click", { cta_id: "estimate_payment", location: "hero" });
                  metaEvent("CTAClick", "custom", { cta_id: "estimate_payment", location: "hero" });
                }}
              >
                <Link to="/calculators">Estimate Your Payment</Link>
              </Button>
            </div>

            {/* Trust pillars */}
            <div className="flex flex-wrap gap-6 pt-4 border-t border-primary-foreground/8">
              {["Licensed Mortgage Professionals", "Secure Digital Application", "Clear, Guided Process"].map((item) => (
                <div key={item} className="flex items-center gap-2">
                  <div className="h-1 w-1 rounded-full bg-silver/40" />
                   <span className="font-body text-sm font-medium uppercase tracking-[0.14em] text-primary-foreground/70">
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Right — calculator card (compact) */}
          <div id="hero-calculator" className="card-static rounded-xl overflow-hidden bg-card border border-border/60 shadow-lg shadow-primary/15">
            <div className="px-6 py-4 border-b border-border/80">
              <p className="font-display text-lg font-semibold text-foreground">Payment Estimator</p>
              <p className="font-body text-sm text-muted-foreground mt-0.5">Estimate your monthly mortgage payment</p>
            </div>

            <div className="p-6 space-y-5">
              <CalcInput label="Home Price" value={homePrice} min={50000} max={2000000} step={5000} format={fmt} onChange={setHomePrice} />
              <CalcInput label="Down Payment" value={downPayment} min={0} max={homePrice * 0.5} step={5000} format={fmt} onChange={setDownPayment} />

              <div>
                <label id="loan-term-label" className="font-body text-sm font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-2 block">
                  Loan Term
                </label>
                <div className="flex gap-2" role="radiogroup" aria-labelledby="loan-term-label">
                  {[15, 20, 30].map((t) => (
                    <button
                      key={t}
                      onClick={() => setTerm(t)}
                      role="radio"
                      aria-checked={term === t}
                      className={`flex-1 rounded-lg py-2 font-body text-sm font-semibold transition-all duration-200 ${
                        term === t
                          ? "bg-primary text-primary-foreground shadow-md"
                          : "bg-secondary text-muted-foreground hover:bg-muted"
                      }`}
                    >
                      {t} yr
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Result */}
            <div className="bg-primary px-6 py-6 text-center">
              <p className="font-body text-sm font-semibold uppercase tracking-[0.2em] text-silver mb-1">
                Estimated Monthly Payment
              </p>
              <p className="font-display text-[3.5rem] font-bold text-primary-foreground md:text-[4rem] leading-none">
                {fmt(monthlyPayment)}
              </p>
              <p className="font-body text-sm font-semibold text-primary-foreground/70 mt-2">
                Estimated Principal and Interest Only
              </p>
              <p className="font-body text-sm text-on-dark-secondary mt-3 max-w-[420px] mx-auto leading-relaxed">
                Taxes, insurance, and HOA are not included unless shown, so your actual payment will be higher. Estimate only, not a loan offer or commitment to lend.
              </p>

              {/* Expandable assumptions disclosure */}
              <div className="mt-3 max-w-[420px] mx-auto">
                <button
                  onClick={() => setAssumptionsOpen(!assumptionsOpen)}
                  aria-expanded={assumptionsOpen}
                  className="inline-flex items-center gap-1 font-body text-sm text-on-dark-tertiary underline underline-offset-2 decoration-on-dark-disclosure hover:text-primary-foreground/80 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-silver/40 focus-visible:ring-offset-2 focus-visible:ring-offset-primary rounded-sm px-1"
                >
                  How this Estimate Works
                  <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${assumptionsOpen ? 'rotate-180' : ''}`} />
                </button>
                {assumptionsOpen && (
                  <p className="font-body text-sm text-on-dark-secondary mt-2 leading-[1.7] text-left">
                    This calculator provides an estimate for educational purposes only. Payment estimates are based on the information entered, a sample interest-rate assumption used internally by Hiler Mortgage, and standard amortization. This estimate is not a loan offer, pre-approval, or commitment to lend. Taxes, homeowners insurance, mortgage insurance, HOA dues, and other property-related costs are not included unless clearly shown. Actual payment, cash to close, fees, and loan terms may vary based on credit profile, loan type, occupancy, property type, loan amount, and other factors.
                  </p>
                )}
              </div>

              <Button
                variant="silver"
                size="lg"
                className="mt-5"
                onClick={() => {
                  gaEvent("cta_click", { cta_id: "see_exact_rate", location: "hero_calculator" });
                  metaEvent("CTAClick", "custom", { cta_id: "see_exact_rate", location: "hero_calculator" });
                  openQuestionnaire();
                }}
              >
                See Your Exact Rate
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-background to-transparent" />
    </section>
  );
};

const CalcInput = ({
  label, value, min, max, step, format, onChange,
}: {
  label: string; value: number; min: number; max: number; step: number;
  format: (v: number) => string; onChange: (v: number) => void;
}) => {
  const inputId = `calc-${label.toLowerCase().replace(/\s+/g, '-')}`;
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div>
      <div className="flex justify-between mb-1.5">
        <label htmlFor={inputId} className="font-body text-sm font-semibold uppercase tracking-[0.14em] text-muted-foreground">{label}</label>
        <span className="font-body text-sm font-bold text-foreground" aria-live="polite">{format(value)}</span>
      </div>
      <div className="relative w-full h-1.5" aria-hidden="true">
        <div className="absolute inset-0 rounded-full bg-border" />
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-primary transition-[width] duration-75"
          style={{ width: `${pct}%` }}
        />
      </div>
      <input
        id={inputId}
        type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full relative -mt-[7px]"
        style={{ background: 'transparent' }}
        /* Native range already exposes min/max/now; only the human-readable
           text needs an override so the tree stays well-formed. */
        aria-valuetext={format(value)}
      />

    </div>
  );
};

export default HeroSection;
